import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TextField, Grid, FormGroup, InputAdornment } from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';
import { toggleRightDrawer, setFocusElement, setReRender } from '../../../store/global/actions';
import { marginBottom } from "../../../config/styles";
import UpdateCancel from "../../atoms/buttons/UpdateCancel";
import { Controller, useForm } from "react-hook-form";
import { basic, validationMessages, requiredOnly, requiredOnlyMessage } from '../../../config/validations';
import { DelegateTypeChangeAlert } from "../alerts/DelegateTypeChangeAlert";
import Alert from '@material-ui/lab/Alert';
import { invokePostService, invokeGetService } from '../../../store/global/actions';
import { validateNonAsciiChars } from '../../../utils/commonUtils';
import { getRightDrawerStatus } from '../../../store/global/selectors';


export default function Details({ element, onCompletion, onClose }) {


    const dispatch = useDispatch();

    const [delegate, setDelegate] = useState(element);
    const [openChangeAlert, setOpenChageAlert] = useState(false);
    const [loading, setLoading] = useState(false);
    const [editedData, setEditedData] = useState(delegate);
    const [network, setNetwork] = useState('');
    const [controlDelegate, setControlDelegate] = useState(element.type === "controller_delegate" ? true : false);
    const [isFetching, setIsFetching] = useState(false);
    const [delegateTypes, setDelegateTypes] = useState([]);

    const rightDrawerStatus = useSelector(getRightDrawerStatus);
    

    const { register, handleSubmit, formState: { errors }, control, reset, setError, watch } = useForm({
        defaultValues: {
            name: element?.name,
            desc: element?.desc,
            type: element?.protocol,
        }
    });


    useEffect(() => {
        setIsFetching(true);
        Promise.all([dispatch(invokeGetService('getDelegateProtocols', [])), 
        dispatch(invokeGetService('getDelegate', [element.id]))]).then(([protocolsResponse, delegateResponse]) => {
            if (protocolsResponse.status && protocolsResponse.data) {
                const modifiedDelegateTypes = protocolsResponse.data.map(item => ({label: item.schemaJson?.desc, value: item.node}));
                const allExistingDelegates = new Set();
                modifiedDelegateTypes.forEach(item => allExistingDelegates.add(item.value))
                if(!allExistingDelegates.has(element.protocol)){
                    const formattedLabel = element.protocol
                    ?.replace(/_/g, " ")                // replace underscores with spaces
                    .replace(/\b\w/g, (char) => char.toUpperCase());
                    modifiedDelegateTypes.push({
                        label: formattedLabel, 
                        value: element.protocol
                    })
                }
                setDelegateTypes(modifiedDelegateTypes);
            }
            if (delegateResponse?.status === true) {
                setDelegate(delegateResponse?.data?.delegate);
                if (delegateResponse?.data?.delegate?.subType === 'controller') {
                    setControlDelegate(true);
                    setDelegateTypes([...delegateTypes, {label:"Controller Delegate", value: "controller_delegate"}]);

                }
                setNetwork(delegateResponse?.data?.network);
                reset({
                    name: delegateResponse?.data?.delegate?.name,
                    desc: delegateResponse?.data?.delegate?.attributes?.desc,
                    type: delegateResponse?.data?.delegate?.attributes?.type,
                    ports: delegateResponse?.data?.delegate?.attributes?.ports ? delegateResponse?.data?.delegate?.attributes?.ports.join(',') : '',
                    network: delegateResponse?.data?.network?.name
                });

            } else {
                if (delegateResponse?.code === 255) {
                    dispatch(toggleRightDrawer());
                    dispatch(setReRender());
                    dispatch(setFocusElement({}));
                }
            }

            setIsFetching(false);
        });
    }, [])

    const messages = {
        ...validationMessages,
        validate: 'This name is already used',
        ...requiredOnlyMessage
    };


    const onClosePopUp = () => {
        setOpenChageAlert(false)
    }

    const cancelUpdation = () => {
        if(rightDrawerStatus) dispatch(toggleRightDrawer());
    }

    const invokeEditDelegateApi = (isForcedEdit, clusterlet, network) => {
        const payload = {
            isForcedEdit,
            clusterlet: clusterlet ? clusterlet : editedData?.clusterlet,
            network: network ? network : editedData?.network
        }
        dispatch(invokePostService('editDelegate', payload, [element.id])).then(res => {
            setLoading(false);
            if (res?.status === true) {
                setOpenChageAlert(false);
                let clusterletData = clusterlet ? clusterlet : editedData?.clusterlet;
                
                let focusElement = { id: delegate._id, type: 'external_function', protocol: clusterletData?.attributes?.type, templateId: '', name: clusterletData?.name, icon: delegate?.icon, desc: clusterletData?.attributes?.desc, provenance: res?.data?.modifiedDelegate?.provenance };
                if(element?.transaction){
                    focusElement.transaction = element.transaction;
                }
                dispatch(setFocusElement(focusElement));

                onCompletion('Delegate', 'edit')
                onClose();
            } else {
                if (res?.data?.rrdefsForDelegate?.length > 0) {
                    setOpenChageAlert(true);
                } else {
                    setOpenChageAlert(false);
                }
                if (res?.code === "600") {
                    res?.errors?.forEach(error => {
                        setError(error.name, { message: error.message });
                    });
                }
                if (res?.code === 255) {
                    if(rightDrawerStatus){
                        dispatch(toggleRightDrawer());
                        dispatch(setReRender());
                        dispatch(setFocusElement({}));
                    } 
                }
            }
        });
    }

    const validateNumbers = (value) => {
        if (!value) {
            return true; // Allow empty value
        }
        const numericValues = value
            .split(',')
            .map((part) => part.trim())
            .filter((part) => /^-?\d+$/.test(part));

        if (numericValues.length === 0 || numericValues.length !== value.split(',').length) {
            return 'Please enter only numeric values separated by commas.';
        }

        return true;
    };

    const onUpdateDelegate = ({ name, desc, type, network, ports }) => {

        setLoading(true);

        const editedDelegate = {
            name,
            attributes: { ...delegate.attributes, type, desc },
            type: 1
        }

        if (ports !== '') {
            editedDelegate.attributes.ports = ports.split(",").map(Number)
        } else {
            if (editedDelegate.attributes.ports !== undefined) delete editedDelegate.attributes.ports
        }

        setEditedData({ clusterlet: editedDelegate, network });
        invokeEditDelegateApi(false, editedDelegate, network);
    }

    return (
        <>
            {controlDelegate && <Grid xs={12} style={marginBottom}>
                <Alert icon={false} severity="info">
                    Controller delegate cannot be edited or removed.
                </Alert>
            </Grid>}

            {openChangeAlert && <DelegateTypeChangeAlert invokeEditDelegateApi={invokeEditDelegateApi} onClose={onClosePopUp} />}

            <Grid xs={12} style={marginBottom}>
                <FormGroup>
                    <TextField
                        name="name"
                        autoFocus
                        label={"Name"}
                        variant={'outlined'}
                        disabled={controlDelegate}
                        fullWidth={true}
                        required={true}
                        inputRef={register({ ...basic })}
                        error={errors?.name ? true : false}
                        helperText={errors?.name ? errors.name?.message ? errors.name.message : messages[errors?.name?.type] : ''}
                    />
                </FormGroup>
            </Grid>


            <Grid xs={12} style={marginBottom}>
                <FormGroup>
                    <TextField
                        id="desc"
                        type={"text"}
                        disabled={controlDelegate}
                        fullWidth
                        rows={4}
                        label={"Description"}
                        placeholder={"Description"}
                        variant="outlined"
                        multiline
                        name="desc"
                        inputRef={register({ required: false, validate: validateNonAsciiChars })}
                        error={errors?.desc}
                        helperText={errors?.desc?.message || ''}
                        InputLabelProps={{ shrink: true }}
                    />
                </FormGroup>
            </Grid>

            {!controlDelegate && <Grid xs={12} style={marginBottom}>
                <Controller
                    name="type"
                    control={control}
                    rules={{ required: true, requiredOnly }}
                    render={({ field, onChange }) =>
                        <TextField
                            {...field}
                            label={'Type'}
                            name="type"
                            disabled={controlDelegate}
                            select
                            fullWidth
                            required
                            variant={'outlined'}
                            defaultValue={controlDelegate ? "controller_delegate" : element?.protocol}
                            onChange={onChange}
                            error={errors?.type ? true : false}
                            helperText={messages[errors?.type?.type] || ''}
                            SelectProps={{
                                MenuProps: {
                                  MenuListProps: {
                                    id: "type-menu-list", 
                                  },
                                },
                              }}
                            InputProps={{
                                startAdornment: isFetching ? (
                                  <InputAdornment position="start" style={{width: "100%"}}>
                                    Loading types, please wait..
                                  </InputAdornment>
                                ) : null,
                              }}
                        >
                            <MenuItem key={123} value={""}>Select type</MenuItem>
                            {delegateTypes.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    }
                />

            </Grid>}

            <Grid xs={12} style={marginBottom}>
                <FormGroup>
                    <TextField
                        name="network"
                        label="Network"
                        disabled={controlDelegate}
                        required={true}
                        variant={'outlined'}
                        //value={network}
                        fullWidth={true}
                        inputRef={register({ ...basic })}
                        error={errors?.network ? true : false}
                        helperText={errors?.network ? messages[errors?.network?.type] : ''}
                        InputLabelProps={{ shrink: true }}
                        data-ui-tour-id='delegate-network-field'
                    />
                </FormGroup>
            </Grid>

            <Grid xs={12} style={marginBottom}>
                <FormGroup>
                    <TextField
                        name="ports"
                        label="Ports"
                        disabled={controlDelegate}
                        required={false}
                        variant={'outlined'}
                        //value={network}
                        fullWidth={true}
                        // inputRef={register({})}
                        inputRef={register({ validate: validateNumbers })}
                        error={errors?.ports ? true : false}
                        helperText={errors?.ports ? errors.ports?.message ? errors.ports.message : messages[errors?.ports?.type] : 'Use commas to sepearte ports. eg: 8080,8068'}
                        InputLabelProps={{ shrink: true }}
                        data-ui-tour-id='delegate-ports-field'

                    />
                </FormGroup>
            </Grid>

            <Grid item xs={12} style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
                <UpdateCancel content={loading ? 'Updating..' : 'Update'} onUpdate={handleSubmit(onUpdateDelegate)} onCancel={cancelUpdation} updateDisable={loading || controlDelegate} typesNotIn={['transaction', 'view']} />
            </Grid>

        </>
    )
}
